package com.schoolconnect.app.feature.academicyears

import com.schoolconnect.app.datasource.network.request.AcademicYearCreateRequest
import com.schoolconnect.app.datasource.network.request.AcademicYearUpdateRequest
import com.schoolconnect.app.datasource.network.response.AcademicYearAnalyticsMetrics
import com.schoolconnect.app.datasource.network.response.AcademicYearAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.AcademicYearWithSchoolResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageTotal
import com.schoolconnect.app.feature.common.toWindow
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.withTotals
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.repository.academicyear.AcademicYearRepository
import com.schoolconnect.app.repository.school.SchoolRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

private data class AcademicYearsPageTotalCount(
    val totalCount: Int,
    val isExact: Boolean
)

class AcademicYearsViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] AcademicYearsViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val reloadDebouncer = DebouncedActionRunner(scope)
    private var loadJob: Job? = null
    private var analyticsJob: Job? = null

    private val _uiState = MutableStateFlow(
        AcademicYearsUiState(
            isLoading = true
        )
    )

    private var schoolLookupByLabel: Map<String, String> = emptyMap()
    private var schoolOptionsByLabel: Set<String> = emptySet()

    val uiState: StateFlow<AcademicYearsUiState> = _uiState.asStateFlow()

    init {
        refreshAcademicYearAnalytics(forceRemote = false)
        refreshAcademicYears(forceRemote = false)
    }

    fun onEvent(event: AcademicYearsUiEvent) {
        when (event) {
            AcademicYearsUiEvent.BackClicked -> Unit
            AcademicYearsUiEvent.RefreshClicked -> {
                refreshAcademicYearAnalytics(forceRemote = true)
                refreshAcademicYears(forceRemote = true)
            }
            is AcademicYearsUiEvent.SearchChanged -> updateFilters(
                searchQuery = event.query,
                resetPage = true
            )
            is AcademicYearsUiEvent.FilterApplied -> {
                _uiState.update {
                    it.copy(
                        filters = event.filters.copy(searchQuery = event.filters.searchQuery),
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = "",
                        openMenuId = null
                    )
                }
                // Force remote so school_name / school_id filters are not served from a stale page cache.
                refreshAcademicYearAnalytics(forceRemote = true)
                refreshAcademicYears(forceRemote = true)
            }
            is AcademicYearsUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                refreshAcademicYears(forceRemote = true)
            }
            is AcademicYearsUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                refreshAcademicYears(forceRemote = false)
            }
            is AcademicYearsUiEvent.RowsPerPageChanged -> {
                _uiState.update {
                    it.copy(paging = it.paging.withPageSize(event.rowsPerPage))
                }
                refreshAcademicYears(forceRemote = false)
            }
            is AcademicYearsUiEvent.MenuToggled -> _uiState.update {
                it.copy(openMenuId = if (it.openMenuId == event.itemId) null else event.itemId)
            }
            AcademicYearsUiEvent.MenuClosed -> _uiState.update { it.copy(openMenuId = null) }
            AcademicYearsUiEvent.AddClicked -> _uiState.update {
                it.copy(
                    dialogMode = AcademicYearsDialogMode.EDITOR,
                    dialogItemId = null,
                    editorDraft = AcademicYearEditorDraft(),
                    openMenuId = null,
                    isEditorSaving = false,
                    errorMessage = "",
                    infoMessage = ""
                )
            }
            is AcademicYearsUiEvent.ViewDetailsClicked -> openDetails(event.itemId)
            is AcademicYearsUiEvent.EditClicked -> openEditor(event.itemId)
            is AcademicYearsUiEvent.ToggleStatusClicked -> openToggleStatusConfirm(event.itemId)
            is AcademicYearsUiEvent.DeleteClicked -> _uiState.update {
                it.copy(
                    dialogMode = AcademicYearsDialogMode.DELETE_CONFIRM,
                    dialogItemId = event.itemId,
                    openMenuId = null
                )
            }
            AcademicYearsUiEvent.DialogDismissed -> _uiState.update {
                it.copy(
                    dialogMode = AcademicYearsDialogMode.NONE,
                    dialogItemId = null,
                    openMenuId = null,
                    isEditorSaving = false,
                    errorMessage = ""
                )
            }
            AcademicYearsUiEvent.DialogSaveRequested -> {
                if (!_uiState.value.isEditorSaving) saveAcademicYear()
            }
            AcademicYearsUiEvent.ToggleStatusConfirmed -> toggleSelectedAcademicYearStatus()
            AcademicYearsUiEvent.DeleteConfirmed -> deleteSelectedAcademicYear()
            is AcademicYearsUiEvent.EditorAcademicYearChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(academicYear = event.value))
            }
            is AcademicYearsUiEvent.EditorSchoolChanged -> updateEditorSchool(event.value)
            is AcademicYearsUiEvent.EditorStartDateChanged -> _uiState.update {
                it.copy(
                    editorDraft = it.editorDraft.copy(
                        startDate = event.value,
                        academicYear = academicYearLabelFromStartDate(event.value)
                    )
                )
            }
            is AcademicYearsUiEvent.EditorEndDateChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(endDate = event.value))
            }
            is AcademicYearsUiEvent.EditorStatusChanged -> _uiState.update {
                it.copy(editorDraft = it.editorDraft.copy(status = event.value))
            }
        }
    }

    private fun updateFilters(
        filters: AcademicYearFilterCriteria = _uiState.value.filters,
        searchQuery: String = _uiState.value.filters.searchQuery,
        resetPage: Boolean
    ) {
        _uiState.update {
            it.copy(
                filters = filters.copy(searchQuery = searchQuery),
                paging = if (resetPage) it.paging.withPageIndex(0) else it.paging,
                errorMessage = "",
                infoMessage = "",
                openMenuId = null
            )
        }
        scheduleAcademicYearsReload()
    }

    private fun scheduleAcademicYearsReload(immediate: Boolean = false) {
        reloadDebouncer.submit(immediate = immediate) {
            refreshAcademicYears(forceRemote = false)
        }
    }

    private fun refreshAcademicYears(forceRemote: Boolean) {
        val filters = _uiState.value.filters
        val schoolFilter = filters.school.trim()
        val startYear = academicYearStartYear(filters.startYear)
        val endYear = academicYearStartYear(filters.endYear)
        val lifecycle = when (filters.status) {
            AcademicYearsFilter.ALL -> null
            AcademicYearsFilter.ACTIVE -> "Current"
            AcademicYearsFilter.UPCOMING -> "Upcoming"
            AcademicYearsFilter.CLOSED -> "Completed"
        }
        val isActive = when (filters.activeStatus) {
            AcademicYearActiveStatusFilter.ALL -> null
            AcademicYearActiveStatusFilter.ACTIVE -> true
            AcademicYearActiveStatusFilter.INACTIVE -> false
        }
        val shouldForceRemote = forceRemote || schoolFilter.isNotBlank()
        val currentState = _uiState.value
        val paging = currentState.paging
        loadJob?.cancel()
        loadJob = scope.launch {
            val resolvedSchoolId = resolveSchoolIdOrLookup(schoolFilter)
            scLog(
                "$TAG refreshAcademicYears start school=${schoolFilter.ifBlank { "<blank>" }} schoolId=${resolvedSchoolId.ifBlank { "<blank>" }} q=${filters.searchQuery.ifBlank { "<blank>" }} status=${filters.status} pageIndex=${paging.pageIndex} pageSize=${paging.pageSize}"
            )
            _uiState.update {
                it.copy(
                    isLoading = true,
                    errorMessage = "",
                    infoMessage = ""
                )
            }

            val result = AcademicYearRepository.loadAcademicYearPage(
                schoolId = resolvedSchoolId.takeIf { it.isNotBlank() },
                // Always send the selected school label so the API filters even when
                // the school has no academic years yet (and is absent from the AY lookup map).
                schoolName = schoolFilter.takeIf { it.isNotBlank() },
                lifecycle = lifecycle,
                query = filters.searchQuery,
                startYear = startYear,
                endYear = endYear,
                createdFrom = appDateInputToApi(filters.createdFrom).orEmpty(),
                createdTo = appDateInputToApi(filters.createdTo).orEmpty(),
                isActive = isActive,
                pageIndex = paging.pageIndex,
                pageSize = paging.pageSize,
                sort = ServerSort(
                    field = _uiState.value.sortField.apiField,
                    order = if (_uiState.value.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                ),
                forceRemote = shouldForceRemote
            )

            result.fold(
                onSuccess = { remoteYears ->
                    schoolLookupByLabel = schoolLookupByLabel + remoteYears.buildSchoolLookup()
                    schoolOptionsByLabel = schoolOptionsByLabel + remoteYears.mapNotNull {
                        it.schoolName?.trim()?.takeIf { name -> name.isNotBlank() }
                    }
                    _uiState.update { state ->
                        state.copy(
                            schoolOptions = schoolOptionsByLabel
                                .sorted()
                        )
                    }
                    val serverTotalCount = remoteYears.serverTotalCountOrNull()
                    val mappedYears = remoteYears
                        .distinctBy { it.id }
                        .map { it.toUiItem() }

                    val estimatedTotalCount = resolveServerPageTotal(
                        exactTotal = serverTotalCount,
                        paging = paging,
                        currentItemCount = mappedYears.size
                    )
                    val nextPaging = paging.withTotals(
                        totalCount = estimatedTotalCount.totalCount,
                        isTotalCountExact = estimatedTotalCount.isExact
                    )
                    val window = nextPaging.toWindow(mappedYears.size)
                    val clampedPageIndex = nextPaging.pageIndex.coerceAtMost(maxOf(0, window.pageCount - 1))

                    if (clampedPageIndex != nextPaging.pageIndex) {
                        _uiState.update { current ->
                            current.copy(
                                paging = current.paging.withPageIndex(clampedPageIndex),
                                isLoading = false
                            )
                        }
                        refreshAcademicYears(forceRemote = shouldForceRemote)
                        return@fold
                    }

                    _uiState.update { current ->
                        current.copy(
                            isLoading = false,
                            items = mappedYears,
                            paging = nextPaging,
                            errorMessage = "",
                            infoMessage = "",
                            dialogMode = when (current.dialogMode) {
                                AcademicYearsDialogMode.NONE -> AcademicYearsDialogMode.NONE
                                else -> current.dialogMode
                            }
                        )
                    }
                    scLog(
                        "$TAG refreshAcademicYears success count=${mappedYears.size} totalCount=${nextPaging.totalCount} exact=${nextPaging.isTotalCountExact}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshAcademicYears cancelled")
                    } else {
                        scLog("$TAG refreshAcademicYears failure", throwable)
                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                errorMessage = throwable.message ?: "Failed to load academic years"
                            )
                        }
                    }
                }
            )
        }
    }

    private fun refreshAcademicYearAnalytics(forceRemote: Boolean) {
        val filters = _uiState.value.filters
        val schoolFilter = filters.school.trim()

        analyticsJob?.cancel()
        analyticsJob = scope.launch {
            val resolvedSchoolId = resolveSchoolIdOrLookup(schoolFilter)
            scLog(
                "$TAG refreshAcademicYearAnalytics start school=${schoolFilter.ifBlank { "<blank>" }} schoolId=${resolvedSchoolId.ifBlank { "<any>" }} forceRemote=$forceRemote"
            )
            _uiState.update {
                it.copy(isAnalyticsLoading = true)
            }

            // Analytics API is school_id-only. If a school label is set but cannot be
            // resolved, do not fall back to global KPIs (list is already school-scoped).
            if (schoolFilter.isNotBlank() && resolvedSchoolId.isBlank()) {
                _uiState.update {
                    it.copy(
                        isAnalyticsLoading = false,
                        academicYearAnalytics = AcademicYearAnalyticsResponse(
                            scope = "school",
                            metrics = AcademicYearAnalyticsMetrics()
                        )
                    )
                }
                scLog("$TAG refreshAcademicYearAnalytics empty school unresolved label=$schoolFilter")
                return@launch
            }

            val result = AcademicYearRepository.loadAcademicYearAnalytics(
                schoolId = resolvedSchoolId.takeIf { it.isNotBlank() },
                forceRemote = forceRemote || schoolFilter.isNotBlank()
            )

            result.fold(
                onSuccess = { analytics ->
                    _uiState.update {
                        it.copy(
                            isAnalyticsLoading = false,
                            academicYearAnalytics = analytics
                        )
                    }
                    scLog(
                        "$TAG refreshAcademicYearAnalytics success total=${analytics.metrics.totalAcademicYears} active=${analytics.metrics.activeYears} inactive=${analytics.metrics.inactiveYears} closed=${analytics.metrics.closedYears} upcoming=${analytics.metrics.upcomingYears} current=${analytics.metrics.currentYears}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshAcademicYearAnalytics cancelled")
                    } else {
                        scLog("$TAG refreshAcademicYearAnalytics failure", throwable)
                        _uiState.update {
                            it.copy(isAnalyticsLoading = false)
                        }
                    }
                }
            )
        }
    }

    private fun openDetails(itemId: String) {
        val item = _uiState.value.items.firstOrNull { it.id == itemId }
        _uiState.update {
            it.copy(
                dialogMode = if (item == null) AcademicYearsDialogMode.NONE else AcademicYearsDialogMode.DETAILS,
                dialogItemId = item?.id,
                editorDraft = item?.toDraft() ?: AcademicYearEditorDraft(),
                openMenuId = null,
                errorMessage = if (item == null) "Academic year not found" else "",
                isEditorSaving = false
            )
        }
    }

    private fun openEditor(itemId: String) {
        val item = _uiState.value.items.firstOrNull { it.id == itemId }
        val draft = item?.toDraft()?.let { itemDraft ->
            itemDraft.copy(
                academicYear = academicYearLabelFromStartDate(itemDraft.startDate)
                    .ifBlank { itemDraft.academicYear }
            )
        }
        _uiState.update {
            it.copy(
                dialogMode = if (item == null) AcademicYearsDialogMode.NONE else AcademicYearsDialogMode.EDITOR,
                dialogItemId = item?.id,
                editorDraft = draft ?: AcademicYearEditorDraft(),
                openMenuId = null,
                errorMessage = if (item == null) "Academic year not found" else "",
                isEditorSaving = false
            )
        }
    }

    private fun openToggleStatusConfirm(itemId: String) {
        val item = _uiState.value.items.firstOrNull { it.id == itemId }
        _uiState.update {
            it.copy(
                dialogMode = if (item == null) AcademicYearsDialogMode.NONE else AcademicYearsDialogMode.TOGGLE_CONFIRM,
                dialogItemId = item?.id,
                openMenuId = null,
                errorMessage = if (item == null) "Academic year not found" else ""
            )
        }
    }

    private fun updateEditorSchool(value: String) {
        val normalizedSchool = value.trim()
        _uiState.update {
            it.copy(
                editorDraft = it.editorDraft.copy(
                    school = normalizedSchool,
                    schoolId = resolveSchoolId(normalizedSchool).orEmpty()
                )
            )
        }
    }

    private fun academicYearLabelFromStartDate(value: String): String {
        val startYear = appDateInputToApi(value)
            ?.substringBefore('-')
            ?.toIntOrNull()
            ?: return ""
        return "$startYear-${((startYear + 1) % 100).toString().padStart(2, '0')}"
    }

    private fun saveAcademicYear() {
        val state = _uiState.value
        val draft = state.editorDraft
        val schoolId = draft.schoolId.ifBlank { resolveSchoolId(draft.school) }
        if (schoolId.isBlank()) {
            val schoolLabel = draft.school.trim()
            if (schoolLabel.isNotBlank()) {
                scope.launch {
                    val resolved = SchoolRepository.loadSchools(query = schoolLabel, isActive = true)
                        .getOrNull()
                        ?.firstOrNull { it.name.orEmpty().equals(schoolLabel, ignoreCase = true) }
                    if (resolved?.id.orEmpty().isNotBlank()) {
                        _uiState.update { it.copy(editorDraft = it.editorDraft.copy(schoolId = resolved?.id.orEmpty())) }
                        saveAcademicYear()
                    } else {
                        _uiState.update { it.copy(errorMessage = "Select a valid school.") }
                    }
                }
                return
            }
            _uiState.update { it.copy(errorMessage = "Select a valid school.") }
            return
        }

        val label = draft.academicYear.trim()
        val startDate = draft.startDate.toApiDate()
        val endDate = draft.endDate.toApiDate()
        if (label.isBlank() || startDate.isBlank() || endDate.isBlank()) {
            _uiState.update {
                it.copy(errorMessage = "Academic year label and dates are required.")
            }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                isEditorSaving = true,
                errorMessage = ""
            )
        }

        scope.launch {
            val result = if (draft.id.isBlank()) {
                AcademicYearRepository.createAcademicYear(
                    AcademicYearCreateRequest(
                        schoolId = schoolId,
                        label = label,
                        startDate = startDate,
                        endDate = endDate,
                        lifecycle = draft.status.apiValue,
                        isActive = true
                    )
                )
            } else {
                AcademicYearRepository.updateAcademicYear(
                    academicYearId = draft.id,
                    request = AcademicYearUpdateRequest(
                        label = label,
                        startDate = startDate,
                        endDate = endDate,
                        lifecycle = draft.status.apiValue,
                        isActive = draft.isActive
                    )
                )
            }

            result.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            dialogMode = AcademicYearsDialogMode.NONE,
                            dialogItemId = null,
                            editorDraft = AcademicYearEditorDraft(),
                            isLoading = false,
                            isEditorSaving = false,
                            errorMessage = ""
                        )
                    }
                    AcademicYearRepository.invalidateCaches()
                    AppToastCenter.showSuccess(
                        if (draft.id.isBlank()) "Academic year added successfully" else "Academic year updated successfully"
                    )
                    refreshAcademicYearAnalytics(forceRemote = true)
                    refreshAcademicYears(forceRemote = true)
                },
                onFailure = { throwable ->
                    scLog("$TAG saveAcademicYear failure", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            isEditorSaving = false,
                            errorMessage = throwable.message ?: "Failed to save academic year"
                        )
                    }
                }
            )
        }
    }

    private fun toggleSelectedAcademicYearStatus() {
        val itemId = _uiState.value.dialogItemId.orEmpty()
        val item = _uiState.value.items.firstOrNull { it.id == itemId }
        if (item == null) {
            _uiState.update { it.copy(errorMessage = "Academic year not found") }
            return
        }

        val isActivating = !item.isActive
        val nextActive = isActivating

        _uiState.update {
            it.copy(
                dialogMode = AcademicYearsDialogMode.NONE,
                dialogItemId = null,
                isLoading = true,
                errorMessage = ""
            )
        }
        scope.launch {
            val result = AcademicYearRepository.updateAcademicYearStatus(
                academicYearId = itemId,
                isActive = nextActive
            )
            result.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            dialogMode = AcademicYearsDialogMode.NONE,
                            dialogItemId = null,
                            infoMessage = if (nextActive) {
                                "Activated ${item.academicYear}"
                            } else {
                                "Inactivated ${item.academicYear}"
                            },
                            isLoading = false
                        )
                    }
                    refreshAcademicYearAnalytics(forceRemote = true)
                    refreshAcademicYears(forceRemote = true)
                },
                onFailure = { throwable ->
                    scLog(
                        "$TAG toggleAcademicYearStatus failure academicYearId=$itemId isActive=$nextActive",
                        throwable
                    )
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to update academic year status"
                        )
                    }
                }
            )
        }
    }

    private fun deleteSelectedAcademicYear() {
        val itemId = _uiState.value.dialogItemId.orEmpty()
        if (itemId.isBlank()) {
            _uiState.update { it.copy(errorMessage = "Academic year not found") }
            return
        }

        _uiState.update {
            it.copy(
                dialogMode = AcademicYearsDialogMode.NONE,
                dialogItemId = null,
                isLoading = true,
                errorMessage = ""
            )
        }
        scope.launch {
            val result = AcademicYearRepository.deleteAcademicYear(itemId)
            result.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            dialogMode = AcademicYearsDialogMode.NONE,
                            dialogItemId = null,
                            editorDraft = AcademicYearEditorDraft(),
                            isLoading = false
                        )
                    }
                    refreshAcademicYearAnalytics(forceRemote = true)
                    refreshAcademicYears(forceRemote = true)
                },
                onFailure = { throwable ->
                    scLog("$TAG deleteAcademicYear failure academicYearId=$itemId", throwable)
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to delete academic year"
                        )
                    }
                }
            )
        }
    }

    private fun AcademicYearWithSchoolResponse.toUiItem(): AcademicYearListItem {
        return AcademicYearListItem(
            id = id.orEmpty(),
            schoolId = schoolId.orEmpty(),
            academicYear = label.orEmpty(),
            school = schoolName.orEmpty().trim().ifBlank { schoolId.orEmpty().ifBlank { "School" } },
            startDate = startDate.orEmpty().toDisplayDate(),
            endDate = endDate.orEmpty().toDisplayDate(),
            status = lifecycle.orEmpty().toAcademicYearStatus(),
            isActive = isActive != false,
            createdOn = createdAt.orEmpty().toDisplayDateTime(),
            updatedOn = updatedAt.orEmpty().toDisplayDateTime(),
            createdBy = createdByName.orEmpty().trim().ifBlank { "—" },
            lastModifiedBy = updatedByName.orEmpty().trim().ifBlank { "—" }
        )
    }

    private fun resolveSchoolId(schoolLabel: String): String {
        val normalized = schoolLabel.trim()
        if (normalized.isBlank()) return ""
        return schoolLookupByLabel[normalized]
            ?: schoolLookupByLabel.entries.firstOrNull { it.key.equals(normalized, ignoreCase = true) }?.value
            .orEmpty()
    }

    /** Resolve from AY-loaded lookup first; fall back to schools API for schools with no AY rows yet. */
    private suspend fun resolveSchoolIdOrLookup(schoolLabel: String): String {
        val cached = resolveSchoolId(schoolLabel)
        if (cached.isNotBlank()) return cached
        val normalized = schoolLabel.trim()
        if (normalized.isBlank()) return ""
        val resolved = SchoolRepository.loadSchools(query = normalized, isActive = null)
            .getOrNull()
            ?.firstOrNull { it.name.orEmpty().equals(normalized, ignoreCase = true) }
        val schoolId = resolved?.id.orEmpty().trim()
        if (schoolId.isNotBlank()) {
            schoolLookupByLabel = schoolLookupByLabel + (normalized to schoolId) + (schoolId to schoolId)
            schoolOptionsByLabel = schoolOptionsByLabel + normalized
        }
        return schoolId
    }

    private fun List<AcademicYearWithSchoolResponse>.buildSchoolLookup(): Map<String, String> {
        return buildMap {
            for (academicYear in this@buildSchoolLookup) {
                val schoolId = academicYear.schoolId.orEmpty().trim()
                val schoolName = academicYear.schoolName.orEmpty().trim()
                if (schoolName.isNotBlank() && schoolId.isNotBlank()) put(schoolName, schoolId)
                if (schoolId.isNotBlank()) put(schoolId, schoolId)
            }
        }
    }

    private fun academicYearStartYear(value: String): Int? {
        val trimmed = value.trim()
        if (trimmed.isBlank()) return null
        return Regex("""\d{4}""").find(trimmed)?.value?.toIntOrNull()
    }

    private fun String.toApiDate(): String {
        return appDateInputToApi(this).orEmpty()
    }

    private fun String.toDisplayDate(): String {
        val trimmed = trim()
        if (trimmed.isBlank()) return ""
        val datePart = trimmed.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return trimmed
        val parts = datePart.split("-")
        if (parts.size != 3) return trimmed
        val year = parts[0]
        val month = parts[1].toIntOrNull() ?: return trimmed
        val day = parts[2].toIntOrNull() ?: return trimmed
        return "${day.toString().padStart(2, '0')} ${shortMonthLabel(month)} $year"
    }

    private fun String.toDisplayDateTime(): String {
        val trimmed = trim()
        if (trimmed.isBlank()) return ""
        val datePart = trimmed.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return trimmed
        val parts = datePart.split("-")
        if (parts.size != 3) return trimmed
        val year = parts[0]
        val month = parts[1].toIntOrNull() ?: return trimmed
        val day = parts[2].toIntOrNull() ?: return trimmed
        return "${day.toString().padStart(2, '0')} ${shortMonthLabel(month)} $year"
    }

    private fun shortMonthLabel(month: Int): String = when (month) {
        1 -> "Jan"
        2 -> "Feb"
        3 -> "Mar"
        4 -> "Apr"
        5 -> "May"
        6 -> "Jun"
        7 -> "Jul"
        8 -> "Aug"
        9 -> "Sep"
        10 -> "Oct"
        11 -> "Nov"
        12 -> "Dec"
        else -> "Mon"
    }

    private fun academicYearsComparator(
        sortField: AcademicYearsSortField,
        ascending: Boolean
    ): Comparator<AcademicYearListItem> {
        val comparator = when (sortField) {
            AcademicYearsSortField.ACADEMIC_YEAR -> compareBySmartText<AcademicYearListItem> { it.academicYear }
            AcademicYearsSortField.SCHOOL -> compareBySmartText<AcademicYearListItem> { it.school }
            AcademicYearsSortField.START_DATE -> compareBy<AcademicYearListItem> { academicYearDateComparable(it.startDate) ?: 0 }
            AcademicYearsSortField.END_DATE -> compareBy<AcademicYearListItem> { academicYearDateComparable(it.endDate) ?: 0 }
            AcademicYearsSortField.LIFECYCLE -> compareBy<AcademicYearListItem> {
                when (it.status) {
                    AcademicYearStatus.ACTIVE -> 0
                    AcademicYearStatus.UPCOMING -> 1
                    AcademicYearStatus.CLOSED -> 2
                }
            }.then(compareBySmartText { it.school })
            AcademicYearsSortField.STATUS -> compareBy<AcademicYearListItem> { if (it.isActive) 0 else 1 }
        }
        return if (ascending) comparator else comparator.reversed()
    }

    private fun academicYearDateComparable(value: String): Int? {
        val trimmed = value.trim()
        if (trimmed.isBlank()) return null
        val parts = trimmed.split(" ")
        if (parts.size == 3) {
            val day = parts[0].toIntOrNull() ?: return null
            val month = when (parts[1].lowercase()) {
                "jan" -> 1
                "feb" -> 2
                "mar" -> 3
                "apr" -> 4
                "may" -> 5
                "jun" -> 6
                "jul" -> 7
                "aug" -> 8
                "sep" -> 9
                "oct" -> 10
                "nov" -> 11
                "dec" -> 12
                else -> return null
            }
            val year = parts[2].toIntOrNull() ?: return null
            return year * 10_000 + month * 100 + day
        }
        return null
    }

    private fun com.schoolconnect.app.feature.common.ServerPageTotalCount.toPageTotalCount(): AcademicYearsPageTotalCount =
        AcademicYearsPageTotalCount(totalCount = totalCount, isExact = isExact)

    fun clear() {
        loadJob?.cancel()
        reloadDebouncer.cancel()
        scope.cancel()
    }
}
